/**
 * ErrorBoundary - Catches React errors and displays fallback UI
 * Production-ready error boundary with logging
 */

import { Component, type ReactNode, type ErrorInfo } from 'react'
import { Button, Result } from 'antd'
import { RefreshCw } from 'lucide-react'
import { t } from '@/config/translations'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
    }
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error to console
    console.error('[ErrorBoundary] Caught error:', error, errorInfo)

    // eslint-disable-next-line no-warning-comments
    // TODO: Send to error reporting service (Sentry, etc.)
    // if (import.meta.env.PROD) {
    //   reportErrorToService(error, errorInfo)
    // }

    this.setState({
      error,
      errorInfo,
    })
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    })
  }

  handleReload = (): void => {
    window.location.reload()
  }

  override render(): ReactNode {
    if (this.state.hasError) {
      // Custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback
      }

      // Default error UI
      return (
        <div
          style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '2rem',
          }}
        >
          <Result
            status="error"
            title={t('connection_error', { error: 'Application Error' })}
            subTitle="Something went wrong. Please try reloading the page."
            extra={[
              <Button key="reset" onClick={this.handleReset}>
                Try Again
              </Button>,
              <Button
                key="reload"
                type="primary"
                icon={<RefreshCw size={16} />}
                onClick={this.handleReload}
              >
                Reload Page
              </Button>,
            ]}
          />

          {/* Show error details in development */}
          {import.meta.env.DEV && this.state.error && (
            <details
              style={{
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                background: '#1f1f1f',
                color: '#fff',
                padding: '1rem',
                maxHeight: '300px',
                overflow: 'auto',
                fontSize: '12px',
                fontFamily: 'monospace',
              }}
            >
              <summary style={{ cursor: 'pointer', marginBottom: '0.5rem' }}>
                Error Details (Development Only)
              </summary>
              <pre>{this.state.error.toString()}</pre>
              <pre>{this.state.errorInfo?.componentStack}</pre>
            </details>
          )}
        </div>
      )
    }

    return this.props.children
  }
}
